import React, { FC } from 'react';

import { Alert, Button, Checkbox, Flex, Form, Input, Layout, message, Row, Select, Space, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { stringToBoolean } from '@app/services/stringToBoolean';
import { AccessDenied } from '@components/AccessDenied';
import { AddressAutocomplete } from '@components/AntFields';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { PageTitle } from '@components/PageTitle';
import { Paper } from '@components/Paper';
import { TABS } from '@pages/Warehouses/pages/Single/interfaces';
import { getWarehouseAddress } from '@pages/Warehouses/services';

import { useCreateWarehouseQuery, useGetWarehouseCustomersQuery } from '../../hooks';

import { IFormValues } from './interfaces';
import { ValidationSchema } from './validations';

export const Add: FC = () => {
  const epdEnabled = stringToBoolean(process.env.REACT_APP_FEATURE_EPD);
  const [form] = Form.useForm<IFormValues>();
  const navigate = useNavigate();
  const commonT = useTranslation(NSKeys.common);
  const warehousesT = useTranslation(NSKeys.warehouses);
  const validationT = useTranslation(NSKeys.validation);
  const getErrorMessage = useApiErrorMessage();
  const warehousesContext = useLocalSelector((state) => state.currentSession.warehousesContext);
  const canCreate = !!warehousesContext?.scope?.$permissions?.create_warehouses;
  const {
    data: customers = [],
    isInitialLoading: isCustomersLoading,
    error,
  } = useGetWarehouseCustomersQuery(canCreate);
  const { mutate: create, status: createStatus, data } = useCreateWarehouseQuery();
  const singleCustomerId = customers.length === 1 ? customers[0].id : undefined;

  React.useEffect(() => {
    if (createStatus === 'success' && data?.uid) {
      navigate(`/dashboard/warehouses/${data?.uid}/${TABS.SETTINGS}`);
    }
  }, [createStatus, data]);

  React.useEffect(() => {
    if (singleCustomerId !== undefined && form.getFieldValue('customer_scope_id') === undefined) {
      form.setFieldValue('customer_scope_id', singleCustomerId);
    }
  }, [singleCustomerId]);

  const goBack = () => navigate('/dashboard/warehouses');

  const handleSubmit = (values: IFormValues) => {
    create(
      {
        name: values.name.trim(),
        customer_scope_id: values.customer_scope_id,
        address: getWarehouseAddress(values.address),
        ...(epdEnabled && { epd_enabled: !!values.epd_enabled }),
      },
      {
        onError: (err) => {
          const { pointerErrors } = getFormErrorsFromResponse(values, err, validationT.t);
          form.setFields(pointerErrors);
          if (!pointerErrors.length) {
            message.error(getErrorMessage(err));
          }
        },
      }
    );
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await ValidationSchema(validationT.t).validateSyncAt(field, { [field]: value });
    },
  };

  if (!warehousesContext) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin />
      </Flex>
    );
  }
  if (!canCreate) {
    return <AccessDenied />;
  }

  let unavailableReason: string | undefined;
  if (error) {
    unavailableReason = getErrorMessage(error);
  } else if (!isCustomersLoading && !customers.length) {
    unavailableReason = warehousesT.t('noWarehouseCustomers');
  }

  return (
    <>
      <Layout.Header>
        <Breadcrumbs />
        <Row justify="space-between" align="middle">
          <PageTitle title={warehousesT.t('titleAdd')} onClick={goBack} />

          <Space size="middle">
            <Button onClick={goBack} size="middle" name="catalog_form" id="catalog_form">
              {commonT.t('buttonCancel')}
            </Button>
            <Button
              type="primary"
              size="middle"
              form="createWarehouse"
              htmlType="submit"
              disabled={!!unavailableReason}
              loading={createStatus === 'loading'}>
              {commonT.t('buttonSave')}
            </Button>
          </Space>
        </Row>
      </Layout.Header>

      <Paper style={{ padding: 24, margin: 24 }}>
        <Flex vertical gap={16}>
          {unavailableReason && <Alert type="warning" showIcon title={unavailableReason} />}
          <Form<IFormValues>
            form={form}
            name="createWarehouse"
            id="createWarehouse"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 8 }}
            disabled={!!unavailableReason}
            initialValues={{ epd_enabled: false }}
            onFinish={handleSubmit}>
            <Form.Item<IFormValues> name="name" required label={warehousesT.t('name')} rules={[yupSync]}>
              <Input placeholder={warehousesT.t('namePlaceholder')} />
            </Form.Item>
            <Form.Item<IFormValues>
              name="customer_scope_id"
              required
              label={warehousesT.t('customer')}
              rules={[yupSync]}>
              <Select
                loading={isCustomersLoading}
                placeholder={warehousesT.t('customerPlaceholder')}
                options={customers.map((customer) => ({ value: customer.id, label: customer.title }))}
              />
            </Form.Item>
            <Form.Item<IFormValues> name="address" rules={[yupSync]} wrapperCol={{ span: 24 }}>
              <AddressAutocomplete
                form={form}
                name={['address']}
                rules={[]}
                required
                hasError={!!form.getFieldError('address').length}
                wrapperCol={{ span: 8 }}
              />
            </Form.Item>
            {epdEnabled && (
              <Form.Item name="epd_enabled" valuePropName="checked" label={warehousesT.t('epdEnabled')}>
                <Checkbox />
              </Form.Item>
            )}
          </Form>
        </Flex>
      </Paper>
    </>
  );
};
